import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth.store';
import { logger } from '@/services/logger';
import toast from 'react-hot-toast';
import { endSessionToLogin, getSessionEpoch, getSessionSignal } from '@/lib/session-boundary';

type SessionTaggedConfig = InternalAxiosRequestConfig & { __sessionEpoch?: number };

export const STALE_SESSION_MESSAGE = 'Dropped: response belongs to a previous session';

/** True when the request was sent under an earlier session (another user / store). */
export function isFromPreviousSession(config: InternalAxiosRequestConfig | undefined | null): boolean {
  const stamped = (config as SessionTaggedConfig | undefined)?.__sessionEpoch;
  return typeof stamped === 'number' && stamped !== getSessionEpoch();
}

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach access token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // T-145 — Read-only impersonation guard: while a super-admin impersonates a
    // tenant with read-only mode on, block all mutating requests client-side so
    // an accidental edit can't alter customer data.
    const { isImpersonating, impersonationReadOnly } = useAuthStore.getState();
    const method = (config.method ?? 'get').toLowerCase();
    if (
      isImpersonating &&
      impersonationReadOnly &&
      ['post', 'put', 'patch', 'delete'].includes(method)
    ) {
      toast.error('Read-only mode — disable it in the impersonation banner to make changes.', {
        id: 'impersonation-readonly',
      });
      return Promise.reject(
        new axios.Cancel('Blocked by read-only impersonation mode'),
      );
    }

    // POS-I109: stamp the request with the session it belongs to and let a session boundary
    // abort it (a caller's own signal, e.g. React-Query's, still wins).
    (config as SessionTaggedConfig).__sessionEpoch = getSessionEpoch();
    if (!config.signal) config.signal = getSessionSignal();

    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const tenantId = useAuthStore.getState().tenant?.id;
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor — handle 401, 403-suspended, and token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token!);
    }
  });
  failedQueue = [];
};

// Track if we've already shown the suspension toast this session to avoid spam
let suspensionToastShown = false;

api.interceptors.response.use(
  (response) => {
    // POS-I109: a response to a request sent under a previous user / store is dropped.
    if (isFromPreviousSession(response.config)) {
      return Promise.reject(new axios.Cancel(STALE_SESSION_MESSAGE));
    }
    return response;
  },
  async (error: AxiosError) => {
    // POS-I109: an error of a previous session's request must not refresh tokens, log out or
    // toast in the new session.
    if (axios.isCancel(error) || isFromPreviousSession(error.config)) {
      return Promise.reject(axios.isCancel(error) ? error : new axios.Cancel(STALE_SESSION_MESSAGE));
    }
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status ?? 0;
    const url = originalRequest?.url ?? 'unknown';
    const responseData = error.response?.data as Record<string, unknown> | undefined;

    // Authentication endpoints: a 401 here means "bad credentials" / "invalid or
    // expired 2FA code" — NOT an expired session. These must surface inline to the
    // caller (toast) instead of triggering the token-refresh + hard redirect below,
    // which would reload the page back to an empty login form mid-2FA.
    const AUTH_ENDPOINTS = ['/auth/login', '/auth/login/2fa', '/auth/register-tenant'];
    const isAuthEndpoint = AUTH_ENDPOINTS.some((p) => url === p || url.endsWith(p));

    // Detect subscription suspension (403 with { suspended: true })
    if (status === 403 && responseData?.suspended === true) {
      useAuthStore.getState().setSubscriptionSuspended(true);
      if (!suspensionToastShown) {
        suspensionToastShown = true;
        // POS-I233: show the server's text (trial-ended or suspended) instead of a hard-coded line
        const serverMessage = typeof responseData.message === 'string' ? responseData.message : '';
        toast.error(
          serverMessage || 'Subscription suspended. All editing is disabled. Contact support to reactivate.',
          { duration: 10000, id: 'subscription-suspended' },
        );
      }
    }

    // POS-I185: the owner did not verify the e-mail in time -> writes are refused with the server's sentence
    if (status === 403 && responseData?.emailUnverified === true) {
      toast.error(
        typeof responseData.message === 'string' ? responseData.message : 'Verify your e-mail to keep making changes.',
        { duration: 8000, id: 'email-unverified' },
      );
    }

    // Log API errors (except 401 which we attempt to refresh)
    if (status !== 401 || originalRequest._retry) {
      logger.apiError(url, status, window.location.pathname, {
        method: originalRequest?.method,
        data: responseData,
      });
    }

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = useAuthStore.getState().refreshToken;
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const refreshEpoch = getSessionEpoch();
        const { data } = await axios.post('/api/v1/auth/refresh', {
          refreshToken,
        });
        // The user signed out / switched while the refresh was in flight: drop its tokens.
        if (getSessionEpoch() !== refreshEpoch) {
          throw new axios.Cancel(STALE_SESSION_MESSAGE);
        }

        useAuthStore.getState().setTokens(data.accessToken, data.refreshToken);
        if (data.user) {
          useAuthStore.getState().setUser(data.user);
        }
        processQueue(null, data.accessToken);
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        if (axios.isCancel(refreshError)) return Promise.reject(refreshError);
        // logout() changes the session owner, which runs the session boundary
        // (lib/session-boundary.ts) before the page goes to /login.
        endSessionToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default api;

/**
 * Open a stored document/file. Files uploaded to object storage are referenced
 * by their storage key and streamed through the authenticated backend proxy
 * (`GET /uploads/view`) so MinIO never needs to be publicly exposed. Legacy
 * rows that hold a real URL are opened directly.
 */
export async function openStoredObject(fileRef: string | null | undefined): Promise<void> {
  if (!fileRef) return;
  if (/^https?:\/\//i.test(fileRef)) {
    window.open(fileRef, '_blank', 'noopener');
    return;
  }
  try {
    const { data } = await api.get('/uploads/view', {
      params: { key: fileRef },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(data as Blob);
    const win = window.open(url, '_blank', 'noopener');
    if (!win) {
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {
    toast.error('Could not open the file.');
  }
}
